import {
  Api,
  getCategoryList,
  recountTotal,
  recountProductsOverlays,
  showOnboardingMessage,
  MOOD_LIST_MAIN,
  MOOD_LIST_ALTERNATIVE,
  ALTERNATIVE_WIDTH,
} from './common';
import { clone } from '../../../../common/common';
import { MOOD_CATEGORY_PLACEHOLDER, REMOVE_PLACEHOLDER_ACTION } from './moodboard-categories';

// ------------------------------------
// Constants
// ------------------------------------
export const MOOD_DRAG_START = 'MOOD_DRAG_START';
export const MOOD_DRAG = 'MOOD_DRAG';
export const MOOD_DROP = 'MOOD_DROP';
export const MOOD_CANCEL_DROP = 'MOOD_CANCEL_DROP';
export const MOOD_DRAG_OVER_CATEGORY = 'MOOD_DRAG_OVER_CATEGORY';

export const MOOD_PLACEHOLDER = 'placeholder';
export const CATEGORY_PRODUCT_HEIGHT = 216;
export const DRAG_CONTAINER_SIZE = 140;

// ------------------------------------
// Common methods
// ------------------------------------

const getProductPosition = event => {
  if (event) {
    let clientRect = event.currentTarget.getClientRects()[0];
    // +50 is needed to make pushing element to the end of category easier
    let position = Math.floor((event.clientY - clientRect.top + 50) / CATEGORY_PRODUCT_HEIGHT);
    if (position < 0) {
      position = 0;
    }
    return position;
  }
  return null;
};

const getAlternativePosition = event => {
  if (event) {
    let clientRect = event.currentTarget.getClientRects()[0];
    let position =
      Math.floor((event.clientX - clientRect.left) / ALTERNATIVE_WIDTH) +
      Math.floor((event.clientY - clientRect.top) / ALTERNATIVE_WIDTH) * 3;
    return position;
  }
};

const getListPosition = (list, event) =>
  list === MOOD_LIST_MAIN ? getProductPosition(event) : getAlternativePosition(event);

export const getDragPointPos = event => {
  let clientRect = event.currentTarget.getClientRects()[0];
  let size = Math.min(clientRect.width, clientRect.height);

  let relativeX = (event.clientX - clientRect.left) / size;
  let relativeY = (event.clientY - clientRect.top) / size;

  let dx = relativeX * (size - DRAG_CONTAINER_SIZE);
  let dy = relativeY * (size - DRAG_CONTAINER_SIZE);

  return {
    offsetX: clientRect.left - event.clientX + dx,
    offsetY: clientRect.top - event.clientY + dy,
  };
};

// ------------------------------------
// Actions
// ------------------------------------

export function dragStart(product, category, list, event) {
  event.preventDefault();

  let originPosition = null;
  if (category) {
    let items = list === MOOD_LIST_MAIN ? category.products : category.alternatives;
    originPosition = items.indexOf(product);
  }

  return {
    type: MOOD_DRAG_START,
    payload: {
      product,
      originCategory: category,
      originList: list,
      originPosition,
      x: event.clientX,
      y: event.clientY,
      ...getDragPointPos(event),
    },
  };
}

const SCROLL_ZONE = 100;
const SCROLL_INTERVAL = 10;
const SCROLL_STEP = 10;
let scrollTimer = null;
export function clearEdgeScroll() {
  window.clearInterval(scrollTimer);
  scrollTimer = null;
}
function edgeScroll(event) {
  if (window.innerHeight - event.clientY < SCROLL_ZONE) {
    if (!scrollTimer) {
      scrollTimer = window.setInterval(() => window.scrollBy(0, SCROLL_STEP), SCROLL_INTERVAL);
    }
  } else if (event.clientY < SCROLL_ZONE) {
    if (!scrollTimer) {
      scrollTimer = window.setInterval(() => window.scrollBy(0, -SCROLL_STEP), SCROLL_INTERVAL);
    }
  } else if (scrollTimer) {
    clearEdgeScroll();
  }
}

let lastDragTime = null;
let dragQueue = 0;
export function drag(event) {
  event.preventDefault();

  edgeScroll(event);

  let time = new Date().getTime();
  if (!lastDragTime || time - lastDragTime > 32) {
    lastDragTime = time;
    dragQueue += 1;
    return {
      type: MOOD_DRAG,
      payload: {
        x: event.clientX,
        y: event.clientY,
      },
    };
  } else {
    return { type: null };
  }
}

export function drop(dragObj, categoryId, list, event) {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      clearEdgeScroll();
      if (event) {
        event.stopPropagation();
      }

      let product = dragObj && dragObj.product;
      let position = getListPosition(list, event);
      dispatch({
        type: MOOD_DROP,
        payload: {
          categoryId,
          list,
          position,
          product: product,
        },
      });

      if (product && dragObj.originCategory) {
        if (categoryId) {
          Api.removeProduct(
            dispatch,
            dragObj.originCategory.id,
            dragObj.originList,
            product,
            data => Api.addProduct(dispatch, categoryId, list, product, position),
          );
        } else {
          dispatch({ type: MOOD_CANCEL_DROP, payload: dragObj });
        }
      } else if (categoryId && product) {
        Api.addProduct(dispatch, categoryId, list, product, position);
      }

      resolve();
    });
  };
}

export function dragOverCategory(categoryId, list, dragObj, state, event) {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      let payload = null;
      if (dragObj && dragObj.product) {
        let position = getListPosition(list, event);
        payload = { categoryId, list, state, position, productId: dragObj.product.id };
      }
      dispatch({ type: MOOD_DRAG_OVER_CATEGORY, payload: payload });
      dispatch({ type: MOOD_CATEGORY_PLACEHOLDER, payload: { action: REMOVE_PLACEHOLDER_ACTION } });
    });
  };
}

// ------------------------------------
// Functions
// ------------------------------------

const processDragStart = (state, action) => {
  let newState = clone(state);
  newState.drag = action.payload;
  if (action.payload.originCategory) {
    newState.categories.forEach(category => {
      if (category.id === action.payload.originCategory.id) {
        let list =
          action.payload.originList === MOOD_LIST_MAIN ? category.products : category.alternatives;
        let index = list.findIndex(i => i.id === action.payload.product.id);
        list.splice(index, 1, MOOD_PLACEHOLDER);
      }
    });
    recountTotal(newState);
  }
  return newState;
};

const processDrag = (state, action) => {
  dragQueue -= 1;
  if (dragQueue === 0) {
    let newState = clone(state);
    newState.drag = {
      ...state.drag,
      x: action.payload.x,
      y: action.payload.y,
    };
    return newState;
  } else {
    return state;
  }
};

const processDrop = (state, action) => {
  const newState = clone(state, { drag: {} });
  const { product, categoryId } = action.payload;
  if (categoryId && product) {
    let processCategory = cat => {
      let list = getCategoryList(cat, action);
      if (cat.id === categoryId && !list.find(item => item.id === product.id)) {
        const index = list.indexOf(MOOD_PLACEHOLDER);
        list.splice(index, 1, clone(product, {quantity: 1}) );
      }
    };
    newState.categories.forEach(processCategory);
    recountTotal(newState);
    recountProductsOverlays(newState);
    showOnboardingMessage(newState);
  }

  return newState;
};

const processCancelDrop = (state, action) => {
  if (action.payload.originCategory) {
    let dragObj = action.payload;
    let newState = clone(state);
    let category = newState.categories.find(category => category.id == dragObj.originCategory.id);
    let list = getCategoryList(category, dragObj.originList);
    list.splice(dragObj.originPosition, 0, dragObj.product);
    recountTotal(newState);
    return newState;
  }

  return state;
};

const processDragOverCategory = (state, action) => {
  let newState = clone(state);
  if (action.payload) {
    newState.categories.forEach(category => {
      if (category.id === action.payload.categoryId) {
        let list = getCategoryList(category, action);
        let index = list.indexOf(MOOD_PLACEHOLDER);
        if (action.payload.state === 'leave' && index !== -1) {
          list.splice(index, 1);
        }

        if (action.payload.state === 'move' && index !== action.payload.position) {
          if (index !== -1) {
            list.splice(index, 1);
          }
          if (!list.find(i => i.id === action.payload.productId)) {
            list.splice(action.payload.position, 0, MOOD_PLACEHOLDER);
          }
        }
      }
    });
  }
  return newState;
};

// ------------------------------------
// Action Handlers
// ------------------------------------
export const DRAG_AND_DROP_ACTION_HANDLERS = {
  [MOOD_DRAG_START]: processDragStart,
  [MOOD_DRAG]: processDrag,
  [MOOD_DROP]: processDrop,
  [MOOD_CANCEL_DROP]: processCancelDrop,
  [MOOD_DRAG_OVER_CATEGORY]: processDragOverCategory,
};



// WEBPACK FOOTER //
// ./app/javascript/design/routes/Moodboard/modules/moodboard-drag-and-drop.js